import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import ExerciseCard from './ExerciseCard';
import remarkGfm from 'remark-gfm';
import LessonCard from './LessonCard';

export interface WorkspaceCard {
  id: string;
  type: 'feedback' | 'exercise' | 'result' | 'summary' | 'vocabulary' | 'skill_assessment' | 'simulation_scorecard' | 'learning_path' | 'lesson';
  content?: string;
  data?: any;
  isLoading?: boolean;
}

interface WorkspacePanelProps {
  isOpen: boolean;
  onClose: () => void;
  cards: WorkspaceCard[];
  onPractice?: () => void;
  onExerciseSubmit?: (exerciseId: string, answer: any) => void;
  onNextExercise?: () => void;
  onOpenDebug?: (anchor: { tool?: string; ts?: number } | undefined) => void;
  onAcceptLearningPath?: (cardId: string) => void;
}

const WorkspacePanel: React.FC<WorkspacePanelProps> = ({ isOpen, onClose, cards, onPractice, onExerciseSubmit, onNextExercise, onOpenDebug, onAcceptLearningPath }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastCardRef = useRef<HTMLDivElement>(null);
  const [dictionaryAddedCards, setDictionaryAddedCards] = React.useState<Set<string>>(new Set());

  // Auto-scroll to center the newest card when cards change
  useEffect(() => {
    if (lastCardRef.current && scrollContainerRef.current && cards.length > 0 && isOpen) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          const card = lastCardRef.current;
          const container = scrollContainerRef.current;
          if (card && container) {
            // Get the card's position relative to the container
            const cardRect = card.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            
            // Calculate the card's position relative to the container's scroll
            const cardTopRelativeToContainer = card.offsetTop - container.scrollTop;
            
            // Only scroll if the card is not already mostly visible
            const cardBottomRelativeToContainer = cardTopRelativeToContainer + cardRect.height;
            const isCardVisible = cardTopRelativeToContainer >= 50 && cardBottomRelativeToContainer <= containerRect.height - 50;
            
            // Only scroll if the container is the workspace panel and card needs scrolling
            if (container.classList.contains('workspace-scroll-container') && !isCardVisible) {
              // Scroll to show the card with some padding from top
              const scrollTo = card.offsetTop - 100; // 100px from top
              container.scrollTo({
                top: Math.max(0, scrollTo),
                behavior: 'smooth'
              });
            }
          }
        }, 300); // Slightly longer delay to ensure card is fully rendered
      });
    }
  }, [cards.length, isOpen]);
  const renderCard = (card: WorkspaceCard, index: number) => {
    const isLastCard = index === cards.length - 1;
    const isDictionaryAdded = dictionaryAddedCards.has(card.id);
    
    const cardContent = (
      <div
        ref={isLastCard ? lastCardRef : undefined}
        className="mb-6 relative"
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          padding: '24px',
          opacity: isLastCard ? 1 : 0.8
        }}
      >
        {/* Debug jump link (only for tool-driven cards) */}
        {onOpenDebug && (card.type === 'vocabulary' || card.type === 'exercise' || card.type === 'skill_assessment' || card.type === 'simulation_scorecard' || card.type === 'feedback') && (
          <button
            onClick={() => onOpenDebug?.(card.data?.debugAnchor)}
            title="View tool call in Debug"
            className="absolute top-2 right-2 text-[11px] px-2 py-0.5 border rounded bg-gray-50 hover:bg-gray-100 text-gray-700"
          >
            Debug
          </button>
        )}
        {card.isLoading ? (
          <div className="flex flex-col items-center justify-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue"></div>
            <p className="mt-4 text-gray-700 text-sm text-center whitespace-pre-wrap">
              {card.content || 'Loading...'}
            </p>
            {card.data?.jobStatusLine && (
              <p className="mt-2 text-[11px] text-gray-500 text-center">
                {card.data.jobStatusLine}
              </p>
            )}
          </div>
        ) : card.type === 'feedback' && card.content ? (
          <>
            <div className="prose prose-blue max-w-none mb-6">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({...props}) => <h1 className="text-2xl font-bold text-talaera-blue mb-4" {...props} />,
                  h2: ({...props}) => <h2 className="text-xl font-semibold text-talaera-blue mb-3" {...props} />,
                  h3: ({...props}) => <h3 className="text-lg font-semibold text-talaera-blue mb-2" {...props} />,
                  p: ({...props}) => <p className="mb-3" {...props} />,
                  table: ({...props}) => <table className="my-4" {...props} />,
                  ul: ({...props}) => <ul className="mb-4 list-disc pl-6" {...props} />,
                  ol: ({...props}) => <ol className="mb-4 list-decimal pl-6" {...props} />,
                  li: ({...props}) => <li className="mb-1" {...props} />,
                  strong: ({...props}) => <strong className="text-gray-900" {...props} />,
                  em: ({...props}) => <em className="text-gray-800" {...props} />,
                }}
              >
                {card.content}
              </ReactMarkdown>
            </div>
          </>
        ) : card.type === 'exercise' && card.data ? (
          <ExerciseCard
            question={card.data.question || card.content || ''}
            options={card.data.options || []}
            exerciseIndex={card.data.exerciseIndex || 0}
            onSubmit={(answer) => onExerciseSubmit && onExerciseSubmit(card.id, answer)}
            showResult={card.data.showResult}
            correctAnswer={card.data.correctAnswer}
            userAnswer={card.data.userAnswer}
            fullExercise={card.data.fullExercise}
          />
        ) : card.type === 'lesson' && card.data ? (
          <LessonCard
            planTitle={card.data.planTitle}
            planId={card.data.planId}
            lessonIndex={card.data.lessonIndex}
            lessonInstance={card.data.lessonInstance}
            completionStatus={card.data.completionStatus}
            onExerciseAnswered={card.data.onExerciseAnswered}
          />
        ) : card.type === 'result' ? (
          <div className="text-center">
            <p className="text-lg mb-4">{card.content}</p>
            {card.data?.showNextButton && onNextExercise && (
              <button
                onClick={onNextExercise}
                style={{
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  paddingTop: '13px',
                  paddingBottom: '13px',
                  background: '#042434',
                  borderRadius: '100px',
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'Lato',
                  fontWeight: '700',
                  wordWrap: 'break-word',
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#063247'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#042434'}
                className="transition-opacity"
              >
                Next Question
              </button>
            )}
          </div>
        ) : card.type === 'summary' ? (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-talaera-blue mb-4">Practice Complete!</h2>
            <p className="text-lg">{card.content}</p>
          </div>
        ) : card.type === 'vocabulary' && card.data ? (
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">{card.data.word}</h3>
            <p className="text-lg text-gray-700 mb-4">{card.data.definition}</p>
            
            {card.data.examples && card.data.examples.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">Examples</h4>
                <ul className="space-y-2">
                  {card.data.examples.map((example: string, i: number) => (
                    <li key={i} className="text-gray-600 italic pl-4 border-l-2 border-gray-300">
                      "{example}"
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {!isDictionaryAdded ? (
              <button
                onClick={() => setDictionaryAddedCards(prev => new Set(prev).add(card.id))}
                style={{
                  width: '100%',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  paddingTop: '13px',
                  paddingBottom: '13px',
                  background: '#042434',
                  borderRadius: '100px',
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'Lato',
                  fontWeight: '700',
                  wordWrap: 'break-word',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#063247'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#042434'}
                className="transition-opacity"
              >
                Add to Personal Dictionary
              </button>
            ) : (
              <p className="text-sm text-gray-500 italic">Added to Personal Dictionary</p>
            )}
          </div>
        ) : card.type === 'skill_assessment' && card.data ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Session Skills Report</h2>
            
            {card.data.motivational_statement && (
              <p className="text-base text-gray-700 mb-6 italic">{card.data.motivational_statement}</p>
            )}
            
            {/* Debug log removed to avoid noise on re-renders */}
            
            {/* Category Progress Bars */}
            <div className="space-y-4 mb-8 mx-auto" style={{ maxWidth: '80%' }}>
              {[
                'Foundations',
                'Share Your Ideas',
                'Understand Others',
                'Conversation Skills',
                'Culture and Context'
              ].map((category) => {
                const categorySkills = card.data.category_scores?.[category];
                const score = categorySkills?.score || 0;
                const wasAssessed = categorySkills?.assessed || false;
                
                return (
                  <div key={category}>
                    <div className="text-base font-semibold text-gray-800 mb-1">{category}</div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      {wasAssessed ? (
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${(score / 10) * 100}%`,
                            background: `linear-gradient(90deg, #8433FF 0%, #01BCD4 100%)`
                          }}
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-xs text-gray-400">
                          Not assessed
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Skills Demonstrated */}
            {card.data.skills_feedback ? (
              <div className="space-y-6">
                {card.data.skills_feedback.strengths && card.data.skills_feedback.strengths.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-green-600">✓</span> Skills Demonstrated Effectively
                    </h3>
                    <div className="space-y-3">
                      {card.data.skills_feedback.strengths.map((skill: any, index: number) => (
                        <div key={index} className="bg-green-50 p-4 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-1">{skill.skill_name}</h4>
                          <p className="text-sm text-gray-700">{skill.description}</p>
                          {skill.example && (
                            <p className="text-sm text-gray-600 italic mt-2">Example: "{skill.example}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {card.data.skills_feedback.improvement && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-amber-600">🎯</span> Area for Improvement
                    </h3>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-1">{card.data.skills_feedback.improvement.skill_name}</h4>
                      <p className="text-sm text-gray-700">{card.data.skills_feedback.improvement.description}</p>
                      {card.data.skills_feedback.improvement.suggestion && (
                        <p className="text-sm text-gray-700 mt-2">
                          <span className="font-medium">Try this:</span> {card.data.skills_feedback.improvement.suggestion}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 p-6 rounded-lg text-center">
                <p className="text-gray-600 italic">
                  We didn't focus on any one skill enough for me to be able to give effective feedback.
                </p>
              </div>
            )}
          </div>
        ) : card.type === 'simulation_scorecard' && card.data ? (
          <div>
            {/* Header with prominent Customer Satisfaction */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{card.data.title || 'Simulation Scorecard'}</h2>
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600">Customer Satisfaction</div>
                <div
                  className="flex items-center justify-center rounded-full text-white"
                  style={{
                    width: '64px', height: '64px',
                    background: 'linear-gradient(135deg, #8433FF 0%, #01BCD4 100%)',
                    boxShadow: '0 6px 16px rgba(132, 51, 255, 0.25)'
                  }}
                >
                  <div className="text-xl font-extrabold">
                    {`${card.data.customer_satisfaction_score}/10`}
                  </div>
                </div>
              </div>
            </div>

            {/* Agent behavior bars (gradient + numbers), vertically stacked like skills summary */}
            <div className="grid grid-cols-1 gap-4 mb-8">
              {[
                { label: 'Calm Under Pressure', key: 'calm_under_pressure' },
                { label: 'De-escalating Language', key: 'deescalating_language' },
                { label: 'Empathy & Understanding', key: 'empathy_and_understanding' }
              ].map((axis) => {
                const val = Number(card.data.agent_behavior_scores?.[axis.key] || 0);
                const pct = Math.max(0, Math.min(100, val * 10));
                return (
                  <div key={axis.key} className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm text-gray-700">{axis.label}</div>
                      <div className="text-sm font-semibold text-gray-800">{val}/10</div>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #8433FF 0%, #01BCD4 100%)' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Criteria cards removed per request (avoid duplication with bars) */}

            {/* Summary */}
            {card.data.summary ? (
              <div className="mb-6">
                <div className="text-base font-semibold text-gray-800 mb-2">Summary</div>
                <p className="text-gray-700">{card.data.summary}</p>
              </div>
            ) : null}

            {/* What the agent did well */}
            {(card.data.highlights && card.data.highlights.length > 0) ? (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="text-green-600">✓</span> What the agent did well
                </h3>
                <ul className="list-disc pl-5 text-gray-700 space-y-1">
                  {card.data.highlights.map((h: string, i: number) => <li key={i}>{h}</li>)}
                </ul>
              </div>
            ) : null}

            {/* What the agent could improve */}
            {((card.data.improvements && card.data.improvements.length) || (card.data.next_steps && card.data.next_steps.length)) ? (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="text-amber-600">🎯</span> What the agent could improve
                </h3>
                <ul className="list-disc pl-5 text-gray-700 space-y-1">
                  {(card.data.improvements || []).map((h: string, i: number) => <li key={`imp-${i}`}>{h}</li>)}
                  {(card.data.next_steps || []).map((h: string, i: number) => <li key={`ns-${i}`}>{h}</li>)}
                </ul>
              </div>
            ) : null}
          </div>
        ) : card.type === 'learning_path' && card.data ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{card.data.title || 'Learning Path'}</h2>
            {(card.data.estimated_time_to_complete || card.data.estimated_schedule) ? (
              <div className="text-sm text-gray-700 mb-4">
                {card.data.estimated_time_to_complete ? (
                  <div><span className="font-semibold">Estimated time:</span> {card.data.estimated_time_to_complete}</div>
                ) : null}
                {card.data.estimated_schedule ? (
                  <div><span className="font-semibold">Schedule:</span> {card.data.estimated_schedule}</div>
                ) : null}
              </div>
            ) : null}

            {Array.isArray(card.data.lessons) && card.data.lessons.length > 0 ? (
              <div className="mb-6 bg-gray-100 rounded-xl p-4 border border-gray-200">
                <div className="relative">
                  {/* vertical connector line */}
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gray-300" />
                  <div className="space-y-4">
                    {card.data.lessons.map((l: any, i: number) => (
                      <div key={i} className="relative pl-10">
                        {/* step indicator */}
                        <div
                          className="absolute left-0 top-4 flex items-center justify-center rounded-full bg-white border border-gray-300 text-gray-800 font-semibold"
                          style={{ width: 30, height: 30, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                        >
                          {i + 1}
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <div className="text-base font-semibold text-gray-900 mb-1">{l?.title || `Lesson ${i + 1}`}</div>
                          {l?.description ? <div className="text-sm text-gray-800 mb-2">{l.description}</div> : null}
                          {l?.after_this_you_can ? (
                            <div className="text-sm text-gray-900">
                              <span className="font-semibold">After this, you’ll be able to:</span> {l.after_this_you_can}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                {card.content ? (
                  <div className="text-sm text-red-700 font-semibold mb-2">{card.content}</div>
                ) : null}
                <div className="text-sm text-gray-600 italic">No lessons returned.</div>
              </div>
            )}

            {card.data.accepted ? (
              <div className="text-sm text-green-700 font-semibold">Accepted</div>
            ) : (
              <button
                onClick={() => onAcceptLearningPath?.(card.id)}
                disabled={
                  !onAcceptLearningPath ||
                  !!card.data.accepting ||
                  !(Array.isArray(card.data.lessons) && card.data.lessons.length > 0)
                }
                style={{
                  width: '100%',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  paddingTop: '13px',
                  paddingBottom: '13px',
                  background: '#042434',
                  borderRadius: '100px',
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'Lato',
                  fontWeight: '700',
                  wordWrap: 'break-word',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  opacity: (onAcceptLearningPath && !card.data.accepting) ? 1 : 0.6
                }}
                className="transition-opacity"
                title={
                  !onAcceptLearningPath
                    ? 'Accept handler not configured'
                    : (!(Array.isArray(card.data.lessons) && card.data.lessons.length > 0)
                      ? 'Cannot accept: learning path has no lessons'
                      : (card.data.accepting ? 'Saving…' : 'Accept this learning path'))
                }
              >
                {card.data.accepting ? 'Saving…' : 'Accept Learning Path'}
              </button>
            )}
          </div>
        ) : null}
      </div>
    );
    
    return cardContent;
  };

  return (
    <div 
      className="absolute top-0 right-0 bottom-0 transition-transform duration-300 ease-in-out overflow-hidden"
      style={{
        width: '65%',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        backgroundColor: '#F5F1EF',
      }}
    >
      {/* Fade overlay at the absolute top */}
      <div 
        className="absolute top-0 left-0 right-0 pointer-events-none z-10"
        style={{
          height: '80px',
          background: 'linear-gradient(to bottom, #F5F1EF 0%, rgba(245, 241, 239, 0.95) 20%, rgba(245, 241, 239, 0.7) 50%, rgba(245, 241, 239, 0) 100%)'
        }}
      />
      
      <button
        onClick={onClose}
        className="absolute top-4 right-8 p-2 rounded-full hover:bg-gray-100 transition-colors bg-white z-20"
        style={{
          color: '#25394D',
          fontSize: '20px',
          lineHeight: '20px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}
      >
        ×
      </button>
      
      <div 
        ref={scrollContainerRef}
        className="h-full overflow-y-auto workspace-scroll-container"
        style={{
          padding: '24px',
        }}
      >
        
        <div className="py-12">
          {cards.map((card, index) => (
            <div key={card.id}>
              {renderCard(card, index)}
            </div>
          ))}
          {/* Add some bottom padding for comfortable scrolling */}
          <div style={{ height: '100px' }} />
        </div>
      </div>
    </div>
  );
};

export default WorkspacePanel;